/**
 * A CÉGEK SZELETE — a #25 első szelete a felületen.
 *
 * ⚠️ EZ AZ EGYETLEN CÉGLISTA A RENDSZERBEN, AMI AZ INAKTÍVAKAT IS MUTATJA.
 * Mindenhol máshol kiesnek (`activeCompanyWhere` a backenden); itt viszont
 * látszaniuk KELL, különben az inaktiválás egyirányú lenne — nem volna
 * felület, amin vissza lehet kapcsolni.
 *
 * ⚠️ ÉS NEM ALAPBÓL. A napi látvány az AKTÍV cégek listája: az inaktívakat a
 * „Inaktívak is" kapcsoló hozza elő, és a láb mindig kiírja, HÁNY van
 * belőlük. Enélkül a kapcsoló egy üres ígéret lenne — a könyvelő nem tudná,
 * érdemes-e megnyomni.
 *
 * ═══ A SZŰRÉS ITT A FELÜLETEN VAN, ÉS EZ KIVÉTEL ═══
 *
 * A repó szabálya, hogy a szűrés a LEKÉRDEZÉSBEN él, nem a képernyőn — a
 * munkalista `scopedWhere`-je és a hiánylista `company: { active: true }`-ja
 * is ezért van a backenden. Itt fordítva helyes, mert ez a szűrő NEM
 * JOGOSULTSÁGOT szűkít, hanem NÉZETET: ugyanazt a halmazt mutatja kevesebb
 * vagy több sorral, és mindkét nézetre joga van annak, aki idáig eljutott.
 * A megkerülése nem nyer semmit.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyContacts } from './CompanyContacts';
import { CompanyForm } from './CompanyForm';
import { ConfirmDialog } from './ConfirmDialog';
import { blockingView } from './companyLoadState';
import { type CompanyRow, useCompanies } from './useCompanies';
import { type LayerRegistrar, useSettingsLayer } from './useSettingsLayer';

/** Amit épp szerkesztünk: egy meglévő sor, vagy egy új cég. */
type Editing = { kind: 'new' } | { kind: 'edit'; company: CompanyRow } | null;

/**
 * A CÉG KAPCSOLATTARTÓI (#32) — a céglista alól nyíló nézet.
 *
 * ⚠️ KÜLÖN ÁLLAPOT, NEM AZ `Editing` HARMADIK ÁGA. A kettőt ugyanaz a
 * `closeForm` zárná, pedig a kapcsolattartó-nézetnek SAJÁT rétege van (űrlap,
 * megerősítő ablak), és azt ő maga jegyzi be a lap regiszterébe. Egy közös ág
 * azt jelentené, hogy az `Escape` a kapcsolattartók nyitott űrlapját a
 * céglistával EGYÜTT zárja — egy lépés helyett kettőt.
 */
type Viewing = { company: CompanyRow } | null;

/** Amit épp meg kell erősíteni — az állapotváltás DÖNTÉS, nem adatbevitel. */
type Confirming = { company: CompanyRow; active: boolean } | null;

type Translate = ReturnType<typeof useTranslation>['t'];

/** A megerősítés szövege — a KÖVETKEZMÉNY, irányonként más. */
function confirmMessage(t: Translate, company: CompanyRow, active: boolean): string {
  return active
    ? t('companies.reactivateConfirm', { company: company.name })
    : t('companies.deactivateConfirm', { company: company.name });
}

/**
 * ILLESZKEDIK-E A KERESÉSRE — tiszta függvény, a komponensen kívül.
 *
 * ⚠️ A KERESÉS AZ ADÓSZÁM JEGYEIRE IS ILLESZT, kötőjel nélkül. A könyvelő a
 * NAV felületéről vagy a számláról másol, és ott mind a három írásmód
 * előfordul (`12345678`, `12345678206`, `12345678-2-06`) — egy szigorú,
 * kötőjeles egyezés a GYAKORI esetben mondana „nincs ilyen cég"-et.
 */
export function matchesQuery(company: CompanyRow, needle: string): boolean {
  if (needle === '') {
    return true;
  }
  // ⚠️ A JEGY-ÁG CSAK AKKOR SZÁMÍT, HA A KERESŐSZÓBAN VAN JEGY.
  //
  // Enélkül a `needle.replace(/\D/g, '')` egy betűs keresésnél ÜRES sztringet
  // ad, az `includes('')` pedig MINDIG igaz — tehát minden cég illeszkedett,
  // és a szűrő némán nem csinált semmit. Pontosan ez volt a 2026-09-21-i
  // bejelentés: a „Pierr" beírása után mind a négy cég ott maradt (#26).
  //
  // A bukásmód azért maradt észrevétlen, mert a NÉV szerinti keresés a
  // gyakori eset, a meglévő teszt viszont csak az adószám JEGYEIT próbálta —
  // ott van jegy, tehát ez az ág sosem üresedett ki.
  const needleDigits = needle.replace(/\D/g, '');
  const digits = company.taxNumber.replace(/\D/g, '');
  return (
    company.name.toLowerCase().includes(needle) ||
    company.taxNumber.toLowerCase().includes(needle) ||
    (needleDigits !== '' && digits.includes(needleDigits))
  );
}

/** A látható sorok — az inaktív-kapcsoló és a kereső együtt. */
export function visibleCompanies(
  companies: CompanyRow[],
  query: string,
  showInactive: boolean
): CompanyRow[] {
  const needle = query.trim().toLowerCase();
  return companies.filter(
    (company) => (company.active || showInactive) && matchesQuery(company, needle)
  );
}

export function CompanySettings({ onLayer }: { onLayer: LayerRegistrar }) {
  const { t } = useTranslation();
  const state = useCompanies();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [viewing, setViewing] = useState<Viewing>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);

  const visible = useMemo(
    () => visibleCompanies(state.companies, query, showInactive),
    [state.companies, query, showInactive]
  );

  const inactiveCount = state.companies.filter((company) => !company.active).length;

  /** Mentés után, „Mégse"-re és `Escape`-re is — az elvetett űrlap hibája nem marad a listán. */
  const closeForm = () => {
    state.clearWriteError();
    setEditing(null);
  };

  const blocked = blockingView(state, t);

  useSettingsLayer(onLayer, {
    hidden: blocked !== null,
    confirming: confirming !== null,
    editing: editing !== null,
    saving: state.status === 'saving',
    closeConfirm: () => setConfirming(null),
    closeForm,
    // A kapcsolattartó-nézet a SAJÁT rétegét jegyzi be — beleértve azt is,
    // hogy nyitott réteg nélkül az `Escape` ide, a céglistára hoz vissza.
    delegated: viewing !== null,
  });

  if (blocked) {
    return blocked;
  }

  if (viewing) {
    return (
      <CompanyContacts
        company={viewing.company}
        onBack={() => setViewing(null)}
        onLayer={onLayer}
      />
    );
  }

  if (editing) {
    return (
      <CompanyForm
        company={editing.kind === 'edit' ? editing.company : null}
        state={state}
        onDone={closeForm}
      />
    );
  }

  return (
    <>
      <div className="settings__toolbar">
        <input
          type="search"
          aria-label={t('companies.searchPlaceholder')}
          placeholder={t('companies.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          {t('companies.showInactive')}
        </label>
        <span className="spacer" />
        <button
          type="button"
          className="settings__primary"
          onClick={() => setEditing({ kind: 'new' })}
        >
          {t('companies.new')}
        </button>
      </div>

      {state.status === 'conflict' || state.status === 'invalid' || state.status === 'failed' ? (
        <p className="error" role="alert">
          {state.status === 'failed' ? t('companies.stateFailed') : state.detail}
        </p>
      ) : null}

      {state.companies.length === 0 || visible.length === 0 ? (
        <p className="empty">
          {t(state.companies.length === 0 ? 'companies.empty' : 'companies.noMatch')}
        </p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>{t('companies.column.name')}</th>
              <th>{t('companies.column.taxNumber')}</th>
              <th className="right">{t('companies.column.retentionYears')}</th>
              <th className="right">{t('companies.column.chaseGraceDays')}</th>
              <th>{t('companies.column.fiscalYear')}</th>
              <th>{t('companies.column.nav')}</th>
              <th>{t('companies.column.state')}</th>
              <th>{/* műveletek — fejléc nélkül, nem adat */}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((company) => (
              <CompanyRowView
                key={company.id}
                company={company}
                onEdit={() => setEditing({ kind: 'edit', company })}
                onContacts={() => setViewing({ company })}
                onToggle={() => setConfirming({ company, active: !company.active })}
              />
            ))}
          </tbody>
        </table>
      )}

      <p className="hint">
        {t('companies.count', { count: state.companies.length })}
        {inactiveCount > 0
          ? ` — ${t('companies.countSplit', {
              active: state.companies.length - inactiveCount,
              inactive: inactiveCount,
            })}`
          : ''}
        {' · '}
        {t('companies.inactiveNote')}
      </p>

      {confirming ? (
        /* ⚠️ MIÉRT VAN EGYÁLTALÁN, amikor a művelet visszafordítható. Mert a
           KÖVETKEZMÉNYE nem látszik a gombon: az inaktiválás kiveszi a céget a
           napi NAV-körből, a hiánylistáról és a munkalistáról — három olyan
           helyről, ami NINCS a képernyőn. A szöveg ezt mondja el, nem azt,
           hogy „biztos vagy benne". */
        <ConfirmDialog
          message={confirmMessage(t, confirming.company, confirming.active)}
          yesLabel={t('companies.confirmYes')}
          noLabel={t('companies.confirmNo')}
          onYes={() => {
            state.setActive(confirming.company.id, confirming.active);
            setConfirming(null);
          }}
          onNo={() => setConfirming(null)}
        />
      ) : null}
    </>
  );
}

function CompanyRowView({
  company,
  onEdit,
  onContacts,
  onToggle,
}: {
  company: CompanyRow;
  onEdit: () => void;
  onContacts: () => void;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    /* ⚠️ AZ INAKTÍV SOR NEM HALVÁNYUL EL, hanem visszahúzott hátteret kap —
       a `design-tokens.md` mérése szerint az `opacity` nem lefokozó csatorna
       (2026-08-31: hét helyről tűnt el szöveg, egy sor 1,52:1-en állt). A
       szöveg teljes erősségű marad. */
    <tr className={company.active ? 'row' : 'row row--inactive'}>
      <td>
        {company.name}
        {company.groupMemberTaxNumber ? (
          <span className="row__note">
            {t('companies.groupMember', { taxNumber: company.groupMemberTaxNumber })}
          </span>
        ) : null}
      </td>
      <td>{company.taxNumber}</td>
      <td className="right">{t('companies.years', { count: company.retentionYears })}</td>
      <td className="right">{t('companies.days', { count: company.chaseGraceDays })}</td>
      <td>
        {company.fiscalYearStartMonth === null
          ? t('companies.fiscalCalendar')
          : t('companies.fiscalMonth', { month: company.fiscalYearStartMonth })}
      </td>
      <td>
        {/* AZ INAKTÍV CÉGNÉL A BEKÖTÉS ÁLLAPOTA NEM TEENDŐ. A sor
            `navTechUserConfigured` mezője a KREDENCIA MEGLÉTE (#25), és a
            kulcs az inaktiválás után is a cégen marad — a „nincs bekötve"
            felirat mellette azt sugallná, hogy van vele dolgunk, pedig a
            NAV-kört amúgy sem futja meg. */}
        {!company.active ? (
          '—'
        ) : company.navTechUserConfigured ? (
          <span className="badge badge--official">{t('companies.navConfigured')}</span>
        ) : (
          <span className="badge badge--attention">{t('companies.navMissing')}</span>
        )}
      </td>
      <td>{company.active ? t('companies.active') : t('companies.inactive')}</td>
      <td className="row__actions">
        <span className="row__buttons">
          <button type="button" onClick={onEdit}>
            {t('companies.edit')}
          </button>
          {/* ⚠️ AZ INAKTÍV CÉGNÉL IS LÁTSZIK, és ez szándékos: a kapcsolattartói
              megmaradnak, és a listájuk olvasható. Felvenni nem lehet hozzá
              (a nézet gombja tiltott), de a meglévőket inaktiválni igen — a
              távozó ember linkje az inaktív cégnél is nála él. */}
          <button type="button" onClick={onContacts}>
            {t('companies.contacts')}
          </button>
          <button type="button" onClick={onToggle}>
            {company.active ? t('companies.deactivate') : t('companies.reactivate')}
          </button>
        </span>
      </td>
    </tr>
  );
}
